import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useRelayEnvironment } from 'react-relay';

import { findPhaseScheduleItem } from '../../functions';
import { RootState } from '../../store';
import { ItemType, MappingItemIdIndexType } from '../../types';

const useControlChildren = (
    values: ItemType,
    bufferItems: React.MutableRefObject<ItemType[]>,
    saveBufferToStore: () => void,
    moveItems: (itemsForMoving: ItemType[], newItems: ItemType[]) => ItemType[],
    historyDeletedChildren: Set<string>,
    updatedItem?: ItemType,
) => {
    const environment = useRelayEnvironment();
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);
    const currentGantItem = useSelector<RootState, ItemType | null>((state) => state.gantItems.currentGantItem);

    const compareArrayAsString = (childrenFirst?: string[], childrenSecond?: string[]) => {
        return childrenFirst?.join('') === childrenSecond?.join('');
    };

    const controlChildren = (nextChildren: string[], previousChildren: string[], previousItems: ItemType[]) => {
        const addedItemsId = nextChildren.filter((value) => !previousChildren.includes(value));
        const deletedItemsId = (previousChildren || []).filter((value) => !nextChildren.includes(value));

        const bufferOfObjectAddItems: { [key: string]: boolean } = {};

        addedItemsId.forEach((id) => {
            bufferOfObjectAddItems[id] = true;
        });

        const itemsForMoving: ItemType[] = [];

        previousItems = previousItems.map((item) => {
            if (currentGantItem?.id === item.id) {
                itemsForMoving.push(item);
                return item;
            } else {
                const newChildren = item.children?.filter((childId) => !bufferOfObjectAddItems[childId]) || [];

                if (compareArrayAsString(newChildren, item.children)) {
                    return item;
                }

                const newItem = {
                    ...item,
                    children: [...newChildren, ...historyDeletedChildren],
                } as ItemType;

                itemsForMoving.push(newItem);
                return newItem;
            }
        });

        const defaultParentParentItem = previousItems[mappingItemIdIndex[currentGantItem?.parentId!]];

        const needAddToParentParentChildren: string[] = [];
        let newItems = previousItems.map((item) => {
            const newItem = { ...item };
            if (addedItemsId.includes(item.id)) {
                newItem.phaseId = findPhaseScheduleItem(currentGantItem?.id!, environment) || undefined;
                newItem.parentId = currentGantItem?.id;
                itemsForMoving.push(newItem);
                return newItem;
            }

            const isDeleteSavingBefore = deletedItemsId.includes(item.id);
            const isDeleteWithoutSaving =
                !addedItemsId.length && !deletedItemsId.length && item.id === currentGantItem?.id;

            if (isDeleteSavingBefore || isDeleteWithoutSaving) {
                newItem.phaseId = findPhaseScheduleItem(currentGantItem?.parentId!, environment) || undefined;
                newItem.parentId = currentGantItem?.parentId;
                needAddToParentParentChildren.push(newItem.id);
                itemsForMoving.push(newItem);
                return newItem;
            }

            return item;
        });

        if (needAddToParentParentChildren.length && defaultParentParentItem) {
            newItems[mappingItemIdIndex[defaultParentParentItem?.id!]] = {
                ...defaultParentParentItem,
                children: [...(defaultParentParentItem?.children || []), ...needAddToParentParentChildren],
            };
        }

        newItems = moveItems(itemsForMoving, newItems);

        return newItems;
    };

    useEffect(() => {
        if (!values || !updatedItem) {
            return;
        }

        const previousItem = bufferItems.current[mappingItemIdIndex[updatedItem.id]];

        if (compareArrayAsString(values.children, previousItem.children)) {
            return;
        }

        const newChildren = values.children || [];

        bufferItems.current = [...bufferItems.current];
        bufferItems.current[mappingItemIdIndex[updatedItem.id]] = {
            ...previousItem,
            children: [...newChildren, ...historyDeletedChildren],
        };
        bufferItems.current = controlChildren(newChildren, previousItem.children || [], bufferItems.current);
        saveBufferToStore();
    }, [values?.children]);

    return [];
};

export default useControlChildren;
